import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useFormBuilderContext } from 'lib/context/FormBuilder/FormBuilderContext';
import { FormBuilderContextProvider } from 'lib/context/FormBuilder/FormBuilderContextProvider';
import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';
import { useCustomForm } from 'lib/hooks/queries/customForms/useCustomForm';
import { useCustomFormTypes } from 'lib/hooks/queries/customForms/useCustomFormTypes';
import { useFormPackets } from 'lib/hooks/queries/forms/useFormPackets';
import usePageTitle from 'lib/hooks/usePageTitle';

import AgTablePage from 'components/shared/AgTablePage/AgTablePage';
import Allowed from 'components/shared/Permissions/Allowed';
import Wizard from 'components/shared/Wizard/Wizard';

import AssignToPacket from './components/AssignToPacket/AssignToPacket';
import Builder from './components/Builder/Builder';
import FooterButtons from './components/FooterButtons';
import NameAndType from './components/NameAndType/NameAndType';
import Review from './components/Review/Review';
import ViewForm from './components/ViewForm';
import HeaderActions from './lib/HeaderActions';
import { DEFAULT_FILTERS } from './lib/defaultFilters';
import { defaultColumns } from './lib/helpers';

const AdminFormBuilder = () => {
  return (
    <Allowed requiredPermissions="form.read">
      <FormBuilderContextProvider>
        <TableContextProvider cols={defaultColumns} name="forms">
          <FormBuilderProcess />
        </TableContextProvider>
      </FormBuilderContextProvider>
    </Allowed>
  );
};

const FormBuilderProcess = () => {
  usePageTitle('Forms');
  const [formToView, setFormToView] = useState({});
  const [viewFormModal, setViewFormModal] = useState(false);
  const { id } = useParams();

  const {
    autoSave,
    saveButtonText,
    breadCrumbs,
    setBreadCrumbs,
    formik,
    openModal,
    setOpenModal,
    handleClose,
    debouncedSearchTerm: debouncedPacketSearchTerm,
    handleSave,
    isFormSavingLoading,
    isEdit,
    practiceId,
    showNavigationButtons,
    handleAutoSave
  } = useFormBuilderContext();

  const { data: formTypesList } = useCustomFormTypes({ params: { practiceId: id } });

  const { data: formPackets } = useFormPackets({
    params: {
      practice_id: practiceId,
      withGraphFetched: { providers: true, services: true, allForms: true },
      filters: { name: debouncedPacketSearchTerm }
    },
    dependencies: [debouncedPacketSearchTerm]
  });

  const typeOptions = useMemo(() => {
    if (formTypesList?.form_functions?.length > 0) {
      return formTypesList.form_functions.map((f) => ({ label: f.name, value: f.id, meta: f }));
    }
    return [];
  }, [formTypesList]);

  const actionViews = {
    name_and_type: {
      component: <NameAndType formik={formik} currentBreadcrumb="name_and_type" />,
      footer: showNavigationButtons ? (
        <FooterButtons
          buttonText="Go to builder"
          nextTo="builder"
          setBreadCrumbs={setBreadCrumbs}
        />
      ) : (
        <FooterButtons />
      )
    },
    builder: {
      component: <Builder formik={formik} currentBreadcrumb="builder" formTypes={typeOptions} />,
      footer: !isEdit ? (
        <FooterButtons
          buttonText="Complete & Review"
          nextTo="review"
          backTo="name_and_type"
          setBreadCrumbs={setBreadCrumbs}
        />
      ) : (
        <FooterButtons buttonText="Submit" isLastStep setBreadCrumbs={setBreadCrumbs} />
      )
    },
    review: {
      component: <Review currentBreadcrumb="review" formTypes={typeOptions} />,
      footer: (
        <FooterButtons
          buttonText="Add to a form packet"
          nextTo="assign_to_packet"
          backTo="builder"
          showSaveAndExit
          setBreadCrumbs={setBreadCrumbs}
        />
      )
    },
    assign_to_packet: {
      component: <AssignToPacket currentBreadcrumb="assign_to_packet" formPackets={formPackets} />,
      footer: (
        <FooterButtons
          buttonText="Submit"
          backTo="review"
          isLastStep
          setBreadCrumbs={setBreadCrumbs}
          showSkip
        />
      )
    }
  };

  const getCurrentView = () => {
    const currentView = breadCrumbs?.find((row) => row.active).view;
    return actionViews[currentView].component;
  };

  const getCurrentFooter = () => {
    const currentView = breadCrumbs?.find((row) => row.active).view;
    return actionViews[currentView].footer;
  };

  const onCellClicked = async (e) => {
    if (e) {
      setFormToView(e);
      setViewFormModal(true);
    }
  };

  return (
    <>
      <AgTablePage
        keepOldFilters
        useData={useCustomForm}
        name="forms"
        defaultColumns={defaultColumns}
        defaultFilters={DEFAULT_FILTERS}
        queryParams={{ practice_id: practiceId }}
        rowStyle={{ cursor: 'pointer' }}
        onSelectRow={(e) => onCellClicked(e)}
        rightHeaderActions={<HeaderActions openModal={() => setOpenModal(true)} />}
      />
      {openModal && (
        <Wizard
          isOpen={openModal}
          handleClose={handleClose}
          handleSaveProgress={handleSave}
          saveProgressLoading={isFormSavingLoading}
          handleAutoSave={handleAutoSave}
          showAutoSave={true}
          showSaveProgress={true}
          title="Form Builder"
          autoSave={autoSave}
          saveButtonText={saveButtonText}
          breadcrumbs={breadCrumbs}
          setBreadCrumbs={setBreadCrumbs}
          footerButtons={getCurrentFooter()}>
          {getCurrentView()}
        </Wizard>
      )}
      {viewFormModal && (
        <ViewForm
          form={formToView}
          setViewFormModal={setViewFormModal}
          viewFormModal={viewFormModal}
        />
      )}
    </>
  );
};

export default AdminFormBuilder;
